<template>
  <div class="page-filter-bar">
    <div
      v-if="showExceptions"
      class="page-filter-bar__exceptions"
    >
      <div v-if="isPageFilterExceptionCsat">
        <SvgIconNonFilter />

        <span> O filtro <strong>Csat</strong> não se aplica a todos os indicadores </span>
      </div>

      <div v-if="isPageFilterExceptionFeeling">
        <SvgIconNonFilter />

        <span> O filtro <strong>Sentimento</strong> não se aplica a todos os indicadores </span>
      </div>

      <div v-if="isPageFilterExceptionStatus">
        <SvgIconNonFilter />

        <span> O filtro <strong>Status</strong> não se aplica a todos os indicadores </span>
      </div>
    </div>

    <TransitionGroup name="transition-fade-long">
      <BaseButton
        v-if="isSmartSummaryButtonVisible()"
        class="page-filter-bar__ai-button"
        key="smart-summary"
        skin="ai"
        @click.native="handleSmartSummaryButtonClick()"
      >
        <SvgIconSparkles />
        Gerar sumário inteligente
      </BaseButton>

      <div
        v-for="tag in tags"
        :key="tag.label"
        class="page-filter-bar__tag"
        :class="{ 'page-filter-bar__tag--link': isTagLink(tag) }"
        @click="link(tag)"
      >
        {{ tag.label }}

        <button
          v-if="tag.type !== 'dateRange'"
          @click.prevent="removeTag(tag)"
        >
          <SvgIconClose />
        </button>
      </div>
    </TransitionGroup>
  </div>
</template>

<script>
  import { mapState, mapMutations, mapGetters, mapActions } from 'vuex'
  import { dateFilter, phoneFilter, cpfFilter, feelingFilter } from '@/utils/filters'
  import BaseButton from '@/components/base/BaseButton'

  export default {
    name: 'CommonPageFilterBar',

    components: {
      BaseButton
    },

    filters: {
      feeling: feelingFilter
    },

    computed: {
      ...mapState('base', [
        'mainApi',
        'leadStatus',
        'platformActions',
        'pageFilter',
        'isPageFilterExceptionCsat',
        'isPageFilterExceptionFeeling',
        'isPageFilterExceptionStatus'
      ]),

      ...mapGetters('smartSummary', ['getAllCardsPrompt']),

      ...mapGetters(['quicksightPlatforms']),

      isQuicksightIntegrated () {
        return this.$auth.user().profile === 'platform'
          && this.quicksightPlatforms.includes(this.$auth.user().platform_name);
      },

      showExceptions() {
        const exceptions = [
          this.isPageFilterExceptionCsat,
          this.isPageFilterExceptionFeeling,
          this.isPageFilterExceptionStatus
        ]

        return exceptions.some(exception => exception === true)
      },

      dateRangeFilter() {
        const [dateStart, dateEnd] = this.pageFilter.dateRange
        const dateRange = []

        if (dateStart && dateEnd) {
          dateRange.push({
            type: 'dateRange',
            label: `${dateFilter(dateStart, 'date')} - ${dateFilter(dateEnd, 'date')}`
          })
        }

        return dateRange
      },

      actionFilter() {
        const { action: filter } = this.pageFilter

        if (!filter) {
          return []
        }

        return filter.map(key => {
          return {
            type: 'action',
            key,
            label: this.platformActions.find(action => action.id_action === key)?.description || ''
          }
        })
      },

      pluginFilter() {
        const { plugin: filter } = this.pageFilter

        if (!filter) {
          return []
        }

        return [
          {
            type: 'plugin',
            label: filter
          }
        ]
      },

      productFilter() {
        const { product: filter } = this.pageFilter

        if (!filter) {
          return []
        }

        return [
          {
            type: 'product',
            label: filter
          }
        ]
      },

      statusFilter() {
        const { status: filter } = this.pageFilter

        if (!filter) {
          return []
        }

        return filter.map(key => {
          return {
            type: 'status',
            key,
            label: this.leadStatus.find(status => status.id === key)?.label || ''
          }
        })
      },

      nameFilter() {
        const { name: filter } = this.pageFilter

        if (!filter) {
          return []
        }

        return [
          {
            type: 'name',
            label: filter
          }
        ]
      },

      emailFilter() {
        const { email: filter } = this.pageFilter

        if (!filter) {
          return []
        }

        return [
          {
            type: 'email',
            label: filter
          }
        ]
      },

      phoneFilter() {
        const { phone: filter } = this.pageFilter

        if (!filter) {
          return []
        }

        return [
          {
            type: 'phone',
            label: phoneFilter(filter)
          }
        ]
      },

      cpfFilter() {
        const { cpf: filter } = this.pageFilter

        if (!filter) {
          return []
        }

        return [
          {
            type: 'cpf',
            label: cpfFilter(filter)
          }
        ]
      },

      csatFilter() {
        const { csat: filter } = this.pageFilter

        if (!filter.length) {
          return []
        }

        return filter.map(key => {
          return {
            type: 'csat',
            key,
            label: `Csat ${key}`
          }
        })
      },

      feelingFilter() {
        const { feeling: filter } = this.pageFilter

        if (!filter.length) {
          return []
        }

        return filter.map(key => {
          return {
            type: 'feeling',
            key,
            label: `Sentimento ${feelingFilter(key)}`
          }
        })
      },

      tags() {
        let tags = [
          ...this.dateRangeFilter,
          ...this.actionFilter,
          ...this.pluginFilter,
          ...this.productFilter,
          ...this.statusFilter,
          ...this.nameFilter,
          ...this.emailFilter,
          ...this.phoneFilter,
          ...this.cpfFilter,
          ...this.csatFilter,
          ...this.feelingFilter
        ]

        return tags
      }
    },

    filters: {
      date: dateFilter,
      phone: phoneFilter,
      cpf: cpfFilter
    },

    methods: {
      ...mapActions('smartSummary', ['fetchSmartSummaryOpenAI', 'createSmartSummary']),

      ...mapMutations('base', {
        setPageFilter: 'SET_PAGE_FILTER',
        setIsPageFilterExceptionCsat: 'SET_IS_PAGE_FILTER_EXCEPTION_CSAT',
        setIsPageFilterExceptionFeeling: 'SET_IS_PAGE_FILTER_EXCEPTION_FEELING',
        setIsPageFilterExceptionStatus: 'SET_IS_PAGE_FILTER_EXCEPTION_STATUS'
      }),

      ...mapMutations('smartSummary', {
        setFilterTags: 'SET_FILTER_TAGS',
        resetSmartSummary: 'RESET_SMART_SUMMARY'
      }),

      isSmartSummaryButtonVisible() {
        const routes = ['insight-conversations']

        if (this.$route.name === 'dashboard') {
          return true
        }

        return routes.includes(this.$route.name)
      },

      handleSmartSummaryButtonClick() {
        this.$root.$emit('pane-tray-close', 'smartSummaryContext')

        setTimeout(() => {
          this.$root.$emit('pane-tray-open', 'smartSummaryContext')
        }, 500)

        this.setFilterTags(this.tags)

        this.fetchSmartSummaryOpenAI()
      },

      isTagLink(tag) {
        const tagsLink = ['dateRange']

        return tagsLink.includes(tag.type)
      },

      link(tag) {
        if (!this.isTagLink(tag)) {
          return
        }

        switch (tag.type) {
          case 'dateRange':
            this.openPageFilter()
        }
      },

      openPageFilter() {
        this.$root.$emit('open-page-filter')
      },

      removeTag(tag) {
        switch (tag.type) {
          case 'dateRange':
            this.removeDateRange()
            break

          case 'action':
            this.removeAction(tag.key)
            break

          case 'plugin':
            this.removePlugin()
            break

          case 'product':
            this.removeProduct()
            break

          case 'status':
            this.removeStatus(tag.key)
            break

          case 'name':
            this.removeName()
            break

          case 'email':
            this.removeEmail()
            break

          case 'phone':
            this.removePhone()
            break

          case 'cpf':
            this.removeCpf()
            break

          case 'csat':
            this.removeCsat(tag.key)
            break

          case 'feeling':
            this.removeFeeling(tag.key)
        }

        this.setIsPageFilterExceptionCsat(false)
        this.setIsPageFilterExceptionFeeling(false)
        this.setIsPageFilterExceptionStatus(false)
      },

      removeDateRange() {
        const pageFilter = {
          dateRange: ''
        }

        this.setPageFilter(pageFilter)
      },

      removeAction(actionId) {
        const action = this.pageFilter.action.filter(action => action !== actionId)
        let pageFilter = { action }

        if (!action.length) {
          pageFilter.action = ''

          this.removePlugin()
        }

        this.setPageFilter(pageFilter)
      },

      removePlugin() {
        const pageFilter = {
          plugin: ''
        }

        this.setPageFilter(pageFilter)
      },

      removeProduct() {
        const pageFilter = {
          product: ''
        }

        this.setPageFilter(pageFilter)
      },

      removeStatus(statusId) {
        const status = this.pageFilter.status.filter(status => status !== statusId)
        let pageFilter = { status }

        if (!status.length) {
          pageFilter.status = ''
        }

        this.setPageFilter(pageFilter)
      },

      removeName() {
        const pageFilter = {
          name: ''
        }

        this.setPageFilter(pageFilter)
      },

      removeEmail() {
        const pageFilter = {
          email: ''
        }

        this.setPageFilter(pageFilter)
      },

      removePhone() {
        const pageFilter = {
          phone: ''
        }

        this.setPageFilter(pageFilter)
      },

      removeCpf() {
        const pageFilter = {
          cpf: ''
        }

        this.setPageFilter(pageFilter)
      },

      removeCsat(evaluation) {
        const csat = this.pageFilter.csat.filter(csat => csat !== evaluation)
        const pageFilter = { csat }

        this.setPageFilter(pageFilter)
      },

      removeFeeling(key) {
        const feeling = this.pageFilter.feeling.filter(feeling => feeling !== key)
        const pageFilter = { feeling }

        this.setPageFilter(pageFilter)
      }
    }
  }
</script>

<style lang="scss" scoped>
  $blue-dark: nth($colors-custom, 1);

  .page-filter-bar {
    margin-bottom: 20px;

    &__exceptions {
      display: flex;
      gap: 10px;
      margin-bottom: 20px;

      > div {
        display: flex;
        height: 30px;
        gap: 5px;
        align-items: center;
        padding: 10px;
        border: 2px solid nth($colors-custom, 2);
        border-radius: 2em;
        font: {
          weight: 500;
          size: 10px;
        }

        strong {
          font-weight: 700;
        }

        svg {
          width: 12px;
          fill: nth($colors-custom, 1);
        }
      }
    }

    > span {
      display: flex;
      flex-wrap: wrap;
      gap: 10px;
    }

    &__tag {
      display: inline-flex;
      height: 30px;
      align-items: center;
      padding: 0 10px;
      border-radius: 2em;
      background: rgba(nth($colors-custom, 2), 0.25);
      color: nth($colors-custom, 1);
      font: {
        weight: 500;
        size: 10px;
      }
      white-space: nowrap;

      &--link {
        cursor: pointer;
      }

      button {
        display: inline-block;
        padding: 5px;
        margin-left: 2px;
        border: 0;
        background: none;

        svg {
          width: 8px;
          fill: rgba(nth($colors-custom, 1), 0.8);
          transform: translateY(-0.5px);
        }
      }
    }

    &__ai-button {
      svg {
        height: 16px;
        width: 16px;
        fill: $blue-dark;
        margin-right: 8px;
      }
    }
  }
</style>
